import { buildSupabaseHeaders, supabase } from '../supabase'
import { resolvePostAuthRedirect } from './postAuthRedirect'
import type { ProgressStatus } from '../../types'

if (!supabase) {
    console.warn('Supabase client not initialized. Auth features will be disabled.');
}

export { supabase }

let pendingRefresh: Promise<string | null> | null = null;

export const getValidAccessToken = async () => {
    if (!supabase) return null
    const client = supabase

    const validateToken = async (token: string | undefined) => {
        if (!token) return false
        const { data, error } = await client.auth.getUser(token)
        return !error && !!data?.user?.id
    }

    const {
        data: { session },
        error,
    } = await client.auth.getSession()
    if (error) return null

    const nowInSeconds = Math.floor(Date.now() / 1000)
    const isTokenExpiring = !session?.expires_at || session.expires_at <= nowInSeconds + 30

    if (session?.access_token && !isTokenExpiring) {
        const isStillValid = await validateToken(session.access_token)
        if (isStillValid) return session.access_token
    }

    if (!pendingRefresh) {
        pendingRefresh = (async () => {
            try {
                const { data: refreshData, error: refreshError } = await client.auth.refreshSession()
                if (refreshError || !refreshData.session?.access_token) return null

                const refreshedToken = refreshData.session.access_token
                const isRefreshedTokenValid = await validateToken(refreshedToken)
                if (!isRefreshedTokenValid) return null

                return refreshedToken
            } finally {
                pendingRefresh = null
            }
        })()
    }

    return pendingRefresh
}

type OAuthProvider = 'github' | 'google' | 'discord'

const loginWithProvider = (provider: OAuthProvider) =>
    supabase?.auth.signInWithOAuth({
        provider,
        options: {
            redirectTo: `${window.location.origin}/auth/callback`
        }
    })

export const loginWithGitHub = () => loginWithProvider('github')
export const loginWithGoogle = () => loginWithProvider('google')
export const loginWithDiscord = () => loginWithProvider('discord')

// Handle OAuth callback — runs on /auth/callback page
export const handleAuthCallback = async () => {
    if (!supabase) return null;

    const accessToken = await getValidAccessToken()
    if (!accessToken) {
        throw new Error('Session unavailable after OAuth callback')
    }

    const { data: authData, error: authError } = await supabase.auth.getUser(accessToken)
    if (authError || !authData?.user) {
        throw authError ?? new Error('Unable to read authenticated user')
    }

    const { data: bootstrapData, error: bootstrapError } = await supabase.functions.invoke('bootstrap-user', {
        body: {},
        headers: buildSupabaseHeaders(accessToken),
    })
    if (bootstrapError) throw bootstrapError
    if (bootstrapData && typeof bootstrapData === 'object' && 'error' in bootstrapData && bootstrapData.error) {
        throw new Error(typeof bootstrapData.error === 'string' ? bootstrapData.error : 'Bootstrap failed')
    }

    // Trigger daily login streak + regen check
    await supabase.functions.invoke('daily-login', {
        body: { user_id: authData.user.id },
        headers: buildSupabaseHeaders(accessToken),
    })

    const { data: progressRows, error: progressError } = await supabase
        .from('user_progress')
        .select('status, modules(slug)')
        .eq('user_id', authData.user.id)

    if (progressError) throw progressError

    const progress: Record<string, ProgressStatus> = {}
    progressRows?.forEach((row: any) => {
        if (row.modules?.slug) {
            progress[row.modules.slug] = row.status
        }
    })

    return {
        id: authData.user.id,
        redirectTo: resolvePostAuthRedirect(progress),
    }
}

// Get current session
export const getSession = () => supabase?.auth.getSession()

// Logout
export const logout = () => supabase?.auth.signOut()
